import { useState, useCallback, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { STATISTICS_CONSTANTS } from '../constants/statistics.constants';

export type QuickDateRangeType = '3M' | '6M' | 'YTD' | '1Y';

export const useDateSelection = (
  initialStartDate?: Dayjs,
  initialEndDate?: Dayjs
) => {
  const { t } = useTranslation();
  const today = dayjs().endOf('day');
  const [startDate, setStartDate] = useState<Dayjs>(
    initialStartDate ||
      dayjs()
        .subtract(STATISTICS_CONSTANTS.DEFAULT_DATE_RANGE_MONTHS, 'month')
        .startOf('month')
  );
  const [endDate, setEndDate] = useState<Dayjs>(
    initialEndDate
      ? initialEndDate.isAfter(today)
        ? today
        : initialEndDate
      : today
  );
  const [dateError, setDateError] = useState('');
  const [quickDateRange, setQuickDateRangeState] =
    useState<QuickDateRangeType | null>(null);

  // Validate date range
  useEffect(() => {
    setDateError('');
    const today = dayjs().endOf('day');
    if (startDate.isAfter(today) || endDate.isAfter(today)) {
      setDateError(
        t(
          'statistics.errors.datesInFuture',
          'Start and End dates cannot be in the future'
        ) || 'Start and End dates cannot be in the future'
      );
    } else if (startDate.isAfter(endDate)) {
      setDateError(
        t(
          'statistics.errors.startDateAfterEndDate',
          'Start date cannot be after End Date'
        ) || 'Start date cannot be after End Date'
      );
    } else if (
      Math.abs(startDate.diff(endDate, 'year')) >
      STATISTICS_CONSTANTS.MAX_DATE_RANGE_YEARS
    ) {
      setDateError(
        t(
          'statistics.errors.dateRange',
          'Date range cannot be more than 5 years apart'
        ) || 'Date range cannot be more than 5 years apart'
      );
    }
  }, [startDate, endDate, t]);

  const setQuickDateRange = useCallback((type: QuickDateRangeType) => {
    const today = dayjs().endOf('day');
    setQuickDateRangeState(type);

    switch (type) {
      case '3M':
        // Last 3 months from today, starting at month boundary
        setStartDate(today.subtract(3, 'month').startOf('month'));
        setEndDate(today);
        break;
      case '6M':
        // Last 6 months from today, starting at month boundary
        setStartDate(today.subtract(6, 'month').startOf('month'));
        setEndDate(today);
        break;
      case 'YTD':
        // Year to date up to today
        setStartDate(dayjs().startOf('year'));
        setEndDate(today);
        break;
      case '1Y':
        // Last 12 months from today, starting at month boundary
        setStartDate(today.subtract(12, 'month').startOf('month'));
        setEndDate(today);
        break;
    }
  }, []);

  const datePickerSlots = {
    nextIconButton: ArrowRightIcon,
    previousIconButton: ArrowLeftIcon,
  };

  // Date picker handlers – set value as chosen so validation can show errors
  const handleStartDateChange = useCallback((date: Dayjs | null) => {
    if (date) {
      setQuickDateRangeState(null);
      setStartDate(date);
    }
  }, []);

  const handleEndDateChange = useCallback((date: Dayjs | null) => {
    if (date) {
      setQuickDateRangeState(null);
      setEndDate(date);
    }
  }, []);

  return {
    startDate,
    setStartDate: handleStartDateChange,
    endDate,
    setEndDate: handleEndDateChange,
    dateError,
    setQuickDateRange,
    quickDateRange,
    datePickerSlots,
  };
};
